import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { GooglePlacesAutocomplete } from 'react-native-google-places-autocomplete';
import { get } from 'lodash';

import { EnvVars } from '../../constants/EnvConfig';
import { GOOGLE_API_PROXY_URL } from '../../utils/geocoding';
import Colors from '../../constants/Colors';
import { StyleSheet, View, Text, Platform } from 'react-native';

let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

function AddressSelector(props) {
  const inputElRef = useRef(null);
  const {
    translation,
    onSelect,
    addressStreet1,
    onChange,
    state,
    city,
    handleForceUpdate,
    isRequired,
    testID,
  } = props;
  const { googlePlacesApiKey } = EnvVars;
  const [inputValue, setInputValue] = useState(addressStreet1 || '');
  const [fields, setFields] = useState(null);
  const [didForceUpdate, setDidForceUpdate] = useState(false);

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(inputValue);
    }
  }, [inputValue]);

  const onSelectPlace = (addressObject) => {
    const user = {};
    const updateUser = (field, value) => {
      user[field] = value;
    };
    if (get(addressObject, 'address_components.0.long_name')) {
      let streetName;
      let streetNumber;
      addressObject.address_components.forEach((component) => {
        if (component.types.indexOf('route') !== -1) {
          streetName = component.short_name;
        } else if (component.types.indexOf('street_number') !== -1) {
          streetNumber = component.short_name;
        } else if (component.types.indexOf('locality') !== -1) {
          updateUser('city', component.short_name);
        } else if (component.types.indexOf('sublocality') !== -1) {
          updateUser('city', component.short_name);
        } else if (component.types.indexOf('administrative_area_level_1') !== -1) {
          updateUser('state', component.short_name);
        } else if (component.types.indexOf('floor') !== -1) {
          updateUser('address_street_1', component.short_name);
        } else if (component.types.indexOf('postal_code') !== -1) {
          updateUser('zip_code', component.short_name);
        }
      });
      if (streetName || streetNumber) {
        const street = [];
        if (streetNumber) street.push(streetNumber);
        if (streetName) street.push(streetName);
        updateUser('address_street_1', street.join(' '));
        setInputValue(street.join(' '));
      }
    } else {
      Sentry.captureException('Places API returned an abnormal payload', {
        extra: {
          res: JSON.stringify(addressObject),
        },
      });
    }
    if (get(addressObject, 'place_id')) {
      updateUser('google_place_id', addressObject.place_id);
    }
    setFields(user);
    onSelect(user);
    handleForceUpdate();
  };

  // There is a rare case where when the fields are empty the parent component doesn't update
  if (!state && !city && fields) {
    if (didForceUpdate) return;
    onSelect(fields);
    setDidForceUpdate(true);
    handleForceUpdate();
  }

  return (
    <View style={styles.container}>
      <Text style={styles.label}>{translation.address_street_1}</Text>
      <GooglePlacesAutocomplete
        disableScroll={true}
        ref={inputElRef}
        placeholder={translation.address_autocomplete_placeholder}
        fetchDetails={true}
        onFail={() => {
          Sentry.captureException('Places Component triggered onFail', {
            extra: {
              input: inputValue,
            },
          });
        }}
        onNotFound={() => {
          Sentry.captureException('Places Component triggered onNotFound', {
            extra: {
              input: inputValue,
            },
          });
        }}
        onPress={(data, details) => {
          onSelectPlace(details);
        }}
        query={{
          key: googlePlacesApiKey,
          language: 'en',
          components: 'country:us',
        }}
        requestUrl={{
          useOnPlatform: 'web',
          url: GOOGLE_API_PROXY_URL,
        }}
        styles={{
          container: {
            // Ensure autocomplete results lists renders above required label
            zIndex: 1,
            minHeight: 60,
            marginTop: 2,
          },
          textInputContainer: {
            borderBottomWidth: 1,
            borderBottomColor: Colors.grayBorder,
          },
          textInput: {
            paddingHorizontal: 0,
            paddingVertical: 0,
            height: 24,
            fontSize: 16,
          },
          listView: {
            position: 'absolute',
            top: 32,
          },
          row: {
            backgroundColor: Colors.lightBackground,
            maxHeight: 32,
            minHeight: 32,
            padding: 0,
            margin: 0,
            height: 32,
          },
        }}
        textInputProps={{
          placeholderTextColor: Colors.mediumGray,
          onChangeText: (t) => {
            // Hacky workaround for a bug with GooglePlacesAutocomplete where it calls onChangeText multiple times with
            // an empty string after the component mounts. We will use a ref so onChangeText only updates the inputValue
            // if the input is focused. See https://github.com/FaridSafi/react-native-google-places-autocomplete/issues/786
            if (inputElRef.current.isFocused()) {
              setInputValue(t);
            }
          },
          value: inputValue,
          testID: testID,
        }}
      />
      {isRequired && (
        <View style={styles.requiredContainer}>
          <Text style={[styles.requiredTextStyle, styles.requiredHelperStyle]}>
            {translation.form_required}
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    minHeight: 70,
  },
  requiredContainer: {
    flexDirection: 'row',
    marginTop: -30,
  },
  requiredTextStyle: {
    color: Colors.brightRed,
    fontWeight: 'bold',
  },
  requiredHelperStyle: {
    fontSize: 10,
  },
  label: {
    color: Colors.veryDarkGray,
  },
});

AddressSelector.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  addressStreet1: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AddressSelector;
